/**
 * Pure conversion of a Glimr centroid into a coarse `device.geo` value. Kept
 * free of atom/store imports so it can be unit-tested in isolation and reused
 * without pulling in the client state graph.
 */

/** Coarse, IP-derived device geo (rounded centroid) for `ortb2.device.geo`. */
export type DeviceGeo = {
  lat: number;
  lon: number;
};

/**
 * Coordinates coarser than this are not worth sending as `device.geo`: a
 * country centroid is misleadingly precise-looking while being useless for
 * targeting. Only county-level or finer is emitted.
 */
const MIN_USEFUL_GEO_LEVELS: ReadonlySet<string> = new Set([
  'county',
  'municipality',
  'city'
]);

/** ~2 decimals ≈ 1.1 km — keeps coordinates clearly coarse (outside TCF SF1). */
const GEO_COORDINATE_DECIMALS = 2;

function roundCoordinate(value: number): number {
  const factor = 10 ** GEO_COORDINATE_DECIMALS;
  return Math.round(value * factor) / factor;
}

/**
 * Converts a Glimr centroid into a coarse `device.geo` value, or `null` when the
 * coordinate is missing, malformed, or too coarse to be useful (country-level).
 */
export function toDeviceGeo(
  coordinate: GlimrCoordinate | null
): DeviceGeo | null {
  if (!coordinate) return null;
  if (!MIN_USEFUL_GEO_LEVELS.has(coordinate.level)) return null;

  const { latitude, longitude } = coordinate;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }

  return {
    lat: roundCoordinate(latitude),
    lon: roundCoordinate(longitude)
  };
}
